import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { TransactionType, CreateTransactionTypeRequest, UpdateTransactionTypeRequest } from '../../../finance/models/TransactionType';
import { getTransactionTypes, createTransactionType, updateTransactionType, deleteTransactionType, deleteTransactionTypes, bulkUpsertTransactionTypes } from './TransactionTypeCRUD';

interface TransactionTypeState {
  transactionTypes: TransactionType[];
  loading: boolean;
  error: string | null;
}

const initialState: TransactionTypeState = {
  transactionTypes: [],
  loading: false,
  error: null,
};

// Async thunk to fetch transaction types
export const fetchTransactionTypesThunk = createAsyncThunk(
  'transactionTypes/fetchTransactionTypes',
  async () => {
    const response = await getTransactionTypes();
    return response.data;
  }
);

// Async thunk to create transaction type
export const createTransactionTypeThunk = createAsyncThunk(
  'transactionTypes/createTransactionType',
  async (data: CreateTransactionTypeRequest, { rejectWithValue }) => {
    try {
      const response = await createTransactionType(data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error);
    }
  }
);

// Async thunk to update transaction type
export const updateTransactionTypeThunk = createAsyncThunk(
  'transactionTypes/updateTransactionType',
  async (data: UpdateTransactionTypeRequest, { rejectWithValue }) => {
    try {
      const response = await updateTransactionType(data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error);
    }
  }
);

// Async thunk to bulk upsert transaction types
export const bulkUpsertTransactionTypesThunk = createAsyncThunk(
  'transactionTypes/bulkUpsertTransactionTypes',
  async (items: UpdateTransactionTypeRequest[], { rejectWithValue }) => {
    try {
      const response = await bulkUpsertTransactionTypes(items);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error);
    }
  }
);

// Async thunk to delete transaction type
export const deleteTransactionTypeThunk = createAsyncThunk(
  'transactionTypes/deleteTransactionType',
  async (id: string) => {
    await deleteTransactionType(id);
    return id;
  }
);

// Async thunk to bulk delete transaction types
export const deleteTransactionTypesThunk = createAsyncThunk(
  'transactionTypes/deleteTransactionTypes',
  async (ids: string[], { rejectWithValue }) => {
    try {
      await deleteTransactionTypes(ids);
      return ids;
    } catch (error: any) {
      return rejectWithValue(error);
    }
  }
);

// Create slice
const transactionTypeSlice = createSlice({
  name: 'transactionTypes',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch transaction types
    builder
      .addCase(fetchTransactionTypesThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTransactionTypesThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.transactionTypes = action.payload;
      })
      .addCase(fetchTransactionTypesThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch transaction types';
      });

    // Create transaction type
    builder
      .addCase(createTransactionTypeThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createTransactionTypeThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.transactionTypes.unshift(action.payload);
      })
      .addCase(createTransactionTypeThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to create transaction type';
      });

    // Update transaction type
    builder
      .addCase(updateTransactionTypeThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateTransactionTypeThunk.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.transactionTypes.findIndex(tt => tt.id === action.payload.id);
        if (index !== -1) {
          state.transactionTypes[index] = action.payload;
        }
      })
      .addCase(updateTransactionTypeThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to update transaction type';
      });

    // Bulk upsert transaction types
    builder
      .addCase(bulkUpsertTransactionTypesThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(bulkUpsertTransactionTypesThunk.fulfilled, (state, action) => {
        state.loading = false;
        if (Array.isArray(action.payload) && action.payload.length > 0) {
          const incomingMap = new Map(action.payload.map((tt: TransactionType) => [tt.id, tt]));
          state.transactionTypes = state.transactionTypes.map((tt) => incomingMap.get(tt.id) || tt);
        }
      })
      .addCase(bulkUpsertTransactionTypesThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to bulk update transaction types';
      });

    // Delete transaction type
    builder
      .addCase(deleteTransactionTypeThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteTransactionTypeThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.transactionTypes = state.transactionTypes.filter(tt => tt.id !== action.payload);
      })
      .addCase(deleteTransactionTypeThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to delete transaction type';
      });

    // Bulk delete transaction types
    builder
      .addCase(deleteTransactionTypesThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteTransactionTypesThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.transactionTypes = state.transactionTypes.filter(tt => !action.payload.includes(tt.id));
      })
      .addCase(deleteTransactionTypesThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to delete transaction types';
      });
  },
});

export const { clearError } = transactionTypeSlice.actions;
export const reducer = transactionTypeSlice.reducer;

// Export actions object for compatibility
export const actions = {
  requestTransactionTypes: () => ({ type: 'transactionTypes/requestTransactionTypes' }),
  fulfillTransactionTypes: (transactionTypes: TransactionType[]) => ({ 
    type: 'transactionTypes/fulfillTransactionTypes', 
    payload: transactionTypes 
  }),
  createTransactionType: (transactionType: TransactionType) => ({ 
    type: 'transactionTypes/createTransactionType', 
    payload: transactionType 
  }),
  updateTransactionType: (transactionType: TransactionType) => ({ 
    type: 'transactionTypes/updateTransactionType', 
    payload: transactionType 
  }),
  deleteTransactionType: (id: string) => ({ 
    type: 'transactionTypes/deleteTransactionType', 
    payload: id 
  }),
};
